import { useState, useEffect } from "react";
import { TextField, Button } from "@mui/material";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { userSlice } from "../redux/user/userSlice";
import { resetPassword } from "../redux/asyncThunk/userActions";
import NewPasswordForm from "../components/NewPasswordForm";
import AuthLayout, { AUTH_FIELD_SX } from "./AuthLayout";
import {
  REQUIRED_FIELDS_MESSAGE,
  validateResetEmailForm,
  sanitizeEmailInput,
} from "../utils/authForms";
import "./index.css";

const { clearMessages } = userSlice;

export default function ResetPage() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.data);
  const messages = useSelector((state) => state.user.messages);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");

  useEffect(() => {
    if (user) {
      navigate("/main");
    }
  }, [navigate, user]);

  const [email, setEmail] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [message, setMessage] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEmail("");
    setFieldErrors({});
    setMessage({ type: "", message: "" });
    dispatch(clearMessages());
  }, [dispatch, location]);

  useEffect(() => {
    const latest = messages[messages.length - 1];
    if (latest?.message) {
      setMessage(latest);
    }
  }, [messages]);

  const handleEmailChange = (event) => {
    setMessage({ type: "", message: "" });
    setFieldErrors({});
    setEmail(sanitizeEmailInput(event.target.value));
  };

  const handleResetPasswordButton = async (event) => {
    event?.preventDefault();
    const errors = validateResetEmailForm(email);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setMessage({
        type: "error",
        message: errors.email === "Укажите email"
          ? REQUIRED_FIELDS_MESSAGE
          : errors.email,
      });
      return;
    }

    setFieldErrors({});
    setMessage({ type: "", message: "" });
    setIsSubmitting(true);

    try {
      await dispatch(resetPassword({ email })).unwrap();
    } catch (err) {
      setMessage({
        type: "error",
        message:
          err?.message || "Не удалось отправить ссылку. Попробуйте позже.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (token) {
    return (
      <AuthLayout
        title="Новый пароль"
        subtitle="Придумайте надёжный пароль для вашего аккаунта"
        footer={
          <>
            <Link to="/login" className="auth-form__link">
              <ArrowBackIcon fontSize="small" />
              Вернуться ко входу
            </Link>
          </>
        }
      >
        <NewPasswordForm token={token} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Восстановление пароля"
      subtitle="Укажите email — мы отправим ссылку для сброса пароля"
      footer={
        <>
          <Link to="/login" className="auth-form__link">
            <ArrowBackIcon fontSize="small" />
            Вернуться ко входу
          </Link>
        </>
      }
    >
      <form onSubmit={handleResetPasswordButton} noValidate>
        <div className="auth-card__body">
          <TextField
            id="email"
            label="Email"
            variant="outlined"
            type="email"
            required
            autoComplete="email"
            sx={AUTH_FIELD_SX}
            value={email}
            onChange={handleEmailChange}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
          />

          {message?.message ? (
            <div
              className={`auth-form__message auth-form__message--${
                message.type === "error" ? "error" : "success"
              }`}
              role="alert"
            >
              {message.message}
            </div>
          ) : null}

          <Button
            type="submit"
            variant="contained"
            className="auth-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Отправка…" : "Отправить ссылку"}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
